import React, { useState, useEffect } from 'react';
import { Button, Card } from '@/components/ui';
import { Invoice } from '../../../types/Invoice';
import InvoiceList from './InvoiceList';
import InvoiceModal from './InvoiceModal';
import { projectService } from '../../../services/projectService';

interface InvoiceTabProps {
  projectId: number;
}

const InvoiceTab: React.FC<InvoiceTabProps> = ({ projectId }) => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | undefined>();
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (projectId) {
      loadInvoices();
    }
  }, [projectId]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const invoiceData = await projectService.getProjectInvoices(projectId);
      setInvoices(invoiceData);
    } catch (error) {
      console.error('Error loading invoices:', error);
      // Handle error appropriately
    } finally {
      setLoading(false);
    }
  };

  const handleAddInvoice = () => {
    setEditingInvoice(undefined);
    setIsModalVisible(true);
  };

  const handleEditInvoice = (invoice: Invoice) => {
    setEditingInvoice(invoice);
    setIsModalVisible(true);
  };

  const handleRetryUpload = async (invoiceId: number, attachmentId: number) => {
    try {
      setRefreshing(true);
      
      // For now, we'll just show a message that they need to delete and recreate
      // In a more sophisticated implementation, we could store the original file and retry
      alert('To retry the upload:\n\n1. Delete this failed attachment\n2. Upload the file again\n\nNote: The filename will be automatically cleaned to avoid upload issues.');
      
    } catch (error) {
      console.error('Error retrying upload:', error);
      alert('Error retrying upload. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  const handleSaveInvoice = async (invoiceData: any, file?: File) => {
    try {
      setRefreshing(true);
      
      if (editingInvoice) {
        // Update existing invoice
        await projectService.updateInvoice(projectId, editingInvoice.id, invoiceData);
      } else {
        // Create new invoice
        const savedInvoice = await projectService.createInvoice(projectId, invoiceData);
        
        // If there's a file, upload it as an attachment
        if (file && savedInvoice?.id) {
          try {
            console.log('Uploading file attachment for invoice:', savedInvoice.id);
            await projectService.uploadInvoiceAttachment(projectId, savedInvoice.id, file);
            console.log('File uploaded successfully');
          } catch (fileError) {
            console.error('Error uploading file:', fileError);
            // Show warning to user but don't fail the operation
            alert('Invoice created successfully, but file upload failed. You can upload the file later manually.');
          }
        }
      }
      
      await loadInvoices();
    } catch (error) {
      console.error('Error saving invoice:', error);
      throw error;
    } finally {
      setRefreshing(false);
    }
  };

  const handleDeleteInvoice = async (invoiceId: number) => {
    try {
      await projectService.deleteInvoice(projectId, invoiceId);
      await loadInvoices();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      throw error;
    }
  };

  const handleViewAttachment = (attachment: any, invoice: Invoice) => {
    // Check if this is a placeholder URL (failed upload)
    if (attachment.attachmentURL && attachment.attachmentURL.startsWith('placeholder://')) {
      alert('File upload failed. This attachment could not be uploaded to storage. Please try uploading the file again.');
      return;
    }
    
    // Call backend endpoint to download the file (same pattern as reports)
    window.open(`/api/projects/${projectId}/invoices/${invoice.id}/attachments/${attachment.id}/download`, '_blank');
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setEditingInvoice(undefined);
  };

  const getTotalInvoiceValue = () => {
    return invoices.reduce((total, invoice) => total + invoice.invoiceValue, 0);
  };

  return (
    <div>
      <Card>
        <Card.Header className="tw-flex tw-justify-between tw-items-center">
          <h6 className="tw-mb-0">Project Invoices</h6>
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-text-content-muted">
              Total: <span className="tw-font-bold">${getTotalInvoiceValue().toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={handleAddInvoice}
              disabled={refreshing}
            >
              <i className="bi bi-plus-circle tw-mr-2"></i>
              Add Invoice
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          <InvoiceList
            invoices={invoices}
            onEdit={handleEditInvoice}
            onDelete={handleDeleteInvoice}
            onViewAttachment={handleViewAttachment}
            onRetryUpload={handleRetryUpload}
            loading={loading}
          />
        </Card.Body>
      </Card>

      <InvoiceModal
        visible={isModalVisible}
        onClose={handleCloseModal}
        onSave={handleSaveInvoice}
        invoice={editingInvoice}
        projectId={projectId}
      />
    </div>
  );
};

export default InvoiceTab;
